import type { ReactNode } from 'react';
import { Play, Pause, Info, Heart, X, Link as LinkIcon } from 'lucide-react';
import { Item } from '../lib/api';

export interface Badge {
  text: string;
  variant: 'default' | 'green' | 'purple';
}

interface SwipeCardTemplateProps {
  item: Item;
  swiping: 'left' | 'right' | null;
  formattedPrice?: string;
  onPlayClick: (e: React.MouseEvent) => void;
  onInfoClick?: (e: React.MouseEvent) => void;
  // Content customization
  title?: string;
  subtitle?: string;
  description?: string;
  badges?: Badge[];
  // Display options
  showPrice?: boolean;
  showInfoButton?: boolean;
  playButtonStyle?: 'default' | 'gradient';
  categoryLabel?: string;
  alwaysShowPlayButton?: boolean;
  isPlaying?: boolean;
  playButtonIcon?: 'play' | 'link';
  mediaContent?: ReactNode;
  showPlayButton?: boolean;
  imageSrc?: string;
}

export function SwipeCardTemplate({ 
  item, 
  swiping, 
  formattedPrice = '',
  onPlayClick, 
  onInfoClick,
  title,
  subtitle,
  description,
  badges = [],
  showPrice = true,
  showInfoButton = true,
  playButtonStyle = 'default',
  categoryLabel,
  alwaysShowPlayButton = false,
  isPlaying = false,
  playButtonIcon = 'play',
  mediaContent,
  showPlayButton,
  imageSrc,
}: SwipeCardTemplateProps) {
  // Default values from item
  const displayTitle = title ?? item.title;
  const displaySubtitle = subtitle ?? (item.creator?.name || 'Digital Artisan');
  const displayDescription = description ?? item.description;
  const displayCategory = categoryLabel ?? item.category;

  // Render badge component
  const renderBadge = (badge: Badge, index: number) => {
    const baseClasses = 'px-3 py-1 rounded-lg text-sm glass backdrop-blur-xl border';
    
    if (badge.variant === 'green') {
      return (
        <div
          key={index}
          className="px-3 py-1 rounded-full border backdrop-blur-xl"
          style={{
            background: 'rgba(0, 255, 136, 0.15)',
            borderColor: 'rgba(0, 255, 136, 0.4)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          }}
        >
          <span style={{ color: '#00FF88', fontSize: '13px' }}>{badge.text}</span>
        </div>
      );
    }
    
    if (badge.variant === 'purple') {
      return (
        <span key={index} className={`${baseClasses} bg-splyt-purple/20 text-splyt-purple border-splyt-purple/30`}>
          {badge.text}
        </span>
      );
    }
    
    // Default variant
    return (
      <span key={index} className={`${baseClasses} text-white/90 border-white/20`}>
        {badge.text}
      </span>
    );
  };

  // Limit badges to 3, show +N if more
  const visibleBadges = badges.slice(0, 3);
  const remainingCount = badges.length - 3;

  // Determine if play button should be shown
  // Priority: explicit showPlayButton > (has media or alwaysShowPlayButton)
  const shouldShowPlayButton = showPlayButton !== undefined 
    ? showPlayButton 
    : Boolean(item.preview_url || alwaysShowPlayButton);

  return (
    <div className="absolute inset-0 rounded-3xl overflow-hidden glass border-2 border-white/10 cursor-grab active:cursor-grabbing touch-none">
      {/* Swipe Indicators */}
      {swiping === 'right' && (
        <div className="absolute inset-0 swipe-like rounded-3xl z-10 flex items-center justify-center">
          <Heart className="w-24 h-24 text-splyt-green" strokeWidth={2.5} fill="currentColor" />
        </div>
      )}

      {swiping === 'left' && (
        <div className="absolute inset-0 swipe-dislike rounded-3xl z-10 flex items-center justify-center">
          <X className="w-24 h-24 text-red-500" strokeWidth={3} />
        </div>
      )}

      {/* Cover Image */}
      <div className="relative w-full h-full">
        <img
          src={imageSrc || item.cover_url}
          alt={displayTitle}
          className="w-full h-full object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayTitle)}&size=800&background=1a1f2e&color=00FF88`;
          }}
        />
        
        {/* Gradient Overlay - ServiceCard style */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 rounded-lg text-sm font-semibold glass backdrop-blur-xl text-white border border-white/20">
            {String(displayCategory)}
          </span>
        </div>
        
        {/* Play Button - Centered, same position for both */}
        {shouldShowPlayButton && !swiping && (
          <button
            onClick={(e) => {
              onPlayClick(e);
            }}
            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center hover:scale-110 transition-all ${
              playButtonStyle === 'gradient' 
                ? 'w-28 h-28 border-4 relative overflow-hidden' 
                : 'w-20 h-20 bg-splyt-green glow-green'
            }`}
            style={playButtonStyle === 'gradient' ? {
              background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.95) 0%, rgba(0, 255, 136, 0.85) 100%)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              boxShadow: '0 0 60px rgba(0, 255, 136, 0.6), 0 8px 30px rgba(0, 0, 0, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.3)',
            } : {}}
          >
            {playButtonStyle === 'gradient' && (
              <div 
                className="absolute inset-0 opacity-20"
                style={{
                  background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.8) 0%, transparent 60%)',
                }}
              />
            )}
            {playButtonIcon === 'link' ? (
              <LinkIcon 
                className={`relative z-10 ${playButtonStyle === 'gradient' ? 'w-14 h-14' : 'w-8 h-8'}`}
                style={{ color: playButtonStyle === 'gradient' ? '#0F1419' : undefined }}
              />
            ) : isPlaying ? (
              <Pause 
                className={`relative z-10 ${playButtonStyle === 'gradient' ? 'w-14 h-14' : 'w-10 h-10'}`}
                style={{ color: playButtonStyle === 'gradient' ? '#0F1419' : undefined }}
                fill={playButtonStyle === 'gradient' ? '#0F1419' : 'currentColor'}
              />
            ) : (
              <Play 
                className={`relative z-10 ${playButtonStyle === 'gradient' ? 'w-14 h-14 ml-2' : 'w-10 h-10 ml-1'}`}
                style={{ color: playButtonStyle === 'gradient' ? '#0F1419' : undefined }}
                fill={playButtonStyle === 'gradient' ? '#0F1419' : 'currentColor'}
              />
            )}
          </button>
        )}
        
        {/* Info Section - Same structure for both */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="space-y-4">
            {mediaContent}

            <div>
              <h2 className="text-2xl font-bold text-white mb-2 line-clamp-2">{displayTitle}</h2>
              <p className="text-white/80 mb-4 line-clamp-1">{displaySubtitle}</p>
              
              {displayDescription && (
                <p className="text-white/70 text-sm mb-4 line-clamp-2">{displayDescription}</p>
              )}
            </div>
            
            {/* Badges */}
            {visibleBadges.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap">
                {visibleBadges.map((badge, index) => renderBadge(badge, index))}
                {remainingCount > 0 && (
                  <span className="px-3 py-1 rounded-lg text-sm glass backdrop-blur-xl text-white/90 border border-white/20">
                    +{remainingCount}
                  </span>
                )}
              </div>
            )}
            
            {/* Bottom Row: Price and Info Button */}
            <div className="flex items-center justify-between">
              {showPrice && formattedPrice && (
                <span className="text-3xl font-bold text-splyt-green">
                  {formattedPrice}
                </span>
              )}
              
              {showInfoButton && onInfoClick && (
                <button
                  onClick={onInfoClick}
                  className="w-12 h-12 rounded-full flex items-center justify-center glass backdrop-blur-xl border border-white/20 hover:scale-110 transition-all ml-auto"
                >
                  <Info className="w-6 h-6 text-white" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
